import {Team} from "../../api";
import {Dispatch, FC, SetStateAction, useCallback, useMemo, useState} from "react";
import {getRelatedTeamIds, useTeamMap} from "@jane/frontend-medewerker/src/components/team-select";
import {usePermissions} from "@jane/frontend-medewerker/src/resources/PermissionsHook";
import {Select} from "../../components/form/Select";

interface TeamSelectProps {
  // data
  teams: Team[]
  primaryTeam: Team|null
  secondaryTeams: Team[]
  // state
  onTeamChanged?: (teamId: Team['id']|null) => void
  onTeamsAndSubTeamsChanged?: (teamIds: Array<Team['id']>|null) => void
}
export const TeamSelect: FC<TeamSelectProps> = props => {
  // All necessary options for team filtering:
  const [teamFilter, setTeamFilter] = useState<string>('-2')
  const teamMap = useTeamMap(props.teams, props.primaryTeam, props.secondaryTeams)
  const {canListAllTeams,canLimitedListAllTeams} = usePermissions()

  const teamOptionsMap: Map<number, [string, number]> = useMemo(() => {
    return new Map([
      [-1, ['Alle teams', 0]],
      ...teamMap.filter(teams => teams[0] !== undefined)
        .map<[Team, number]>(teams => [teams.slice(-1)[0], teams.length])
        .map<[number, [string, number]]>(([team, indentation]) => [team.id, [team.name, indentation]])
    ])
  }, [teamMap])

  const teamFilterOptions: [string,string][] = useMemo(() => {
    return Array.from(teamOptionsMap)
      .filter(([id]: [number, [string, number]]): boolean => {
        return !(canListAllTeams && id === -1)
      })
      .map(([id, [name, indentation]]: [number, [string, number]]): [string, string] => {
        return [id.toString(), (canListAllTeams ? "\xA0\xA0".repeat(indentation) : "") + name]
      })
  }, [teamOptionsMap, canListAllTeams])

  /** Used to look up related teams */
  const relatedTeamsMap: {[teamId: string]: string[]} = useMemo(() => {
    return Object.fromEntries(props.teams.map((team) => {
      return [team.id.toString(), getRelatedTeamIds(team, teamMap).map(id => id.toString())]
    }))
  }, [teamMap, props.teams])

  const onChange = useCallback((teamId: string) => {
    setTeamFilter(teamId)
    props.onTeamChanged?.(teamId === '-1' ? null : parseInt(teamId))
    props.onTeamsAndSubTeamsChanged?.(teamId === '-1' ? null : (relatedTeamsMap[teamId]?.map(id => parseInt(id)) ?? []))
  }, [relatedTeamsMap, props.onTeamChanged, props.onTeamsAndSubTeamsChanged])

  return <Select options={teamFilterOptions} value={teamFilter} onChange={onChange} label={''} />
}